import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import DemoBanner from "@/components/DemoBanner";
import "@/index.css";
import { AuthProvider, useAuth, homePath } from "@/lib/auth.jsx";
import { TourProvider } from "@/lib/tour.jsx";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Home from "@/pages/Home";
import Discover from "@/pages/Discover";
import DevelopmentDetail from "@/pages/DevelopmentDetail";
import Portfolio from "@/pages/Portfolio";
import Messages from "@/pages/Messages";
import Help from "@/pages/Help";
import Profile from "@/pages/Profile";
import DeveloperDashboard from "@/pages/DeveloperDashboard";
import DeveloperProperties from "@/pages/DeveloperProperties";
import DeveloperLeads from "@/pages/DeveloperLeads";
import AuthCallback from "@/pages/AuthCallback";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AdminReview from "@/pages/AdminReview";
import AdminSupport from "@/pages/AdminSupport";
import UserProfile from "@/pages/UserProfile";
import DeveloperProfile from "@/pages/DeveloperProfile";
import CompanyVerification from "@/pages/CompanyVerification";
import InvestorGuide from "@/pages/InvestorGuide";
import AdminDeveloperView from "@/pages/AdminDeveloperView";

function Protected({ children, role, admin }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (admin && !user.is_admin) return <Navigate to={homePath(user)} replace />;
  if (role && user.role !== role) {
    // redirect to the right home for their role
    return <Navigate to={homePath(user)} replace />;
  }
  return <Layout>{children}</Layout>;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homePath(user)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <TourProvider>
          <Toaster position="top-right" theme="light" richColors />
          <DemoBanner />
          <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/auth-callback" element={<AuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/admin/review" element={<AdminReview />} />

          {/* Investor */}
          <Route path="/home" element={<Protected role="investor"><Home /></Protected>} />
          <Route path="/discover" element={<Protected><Discover /></Protected>} />
          <Route path="/development/:id" element={<Protected><DevelopmentDetail /></Protected>} />
          <Route path="/developer/:id" element={<Protected><DeveloperProfile /></Protected>} />
          <Route path="/portfolio" element={<Protected role="investor"><Portfolio /></Protected>} />
          <Route path="/help" element={<Protected><Help /></Protected>} />
          <Route path="/investor-guide" element={<Protected><InvestorGuide /></Protected>} />
          {/* Shared */}
          <Route path="/messages" element={<Protected><Messages /></Protected>} />
          <Route path="/profile" element={<Protected><Profile /></Protected>} />

          {/* Developer */}
          <Route path="/dashboard" element={<Protected role="developer"><DeveloperDashboard /></Protected>} />
          <Route path="/properties" element={<Protected role="developer"><DeveloperProperties /></Protected>} />
          <Route path="/verification" element={<Protected role="developer"><CompanyVerification /></Protected>} />
          <Route path="/leads" element={<Protected role="developer"><DeveloperLeads /></Protected>} />
          <Route path="/admin" element={<Protected admin><AdminSupport /></Protected>} />
          <Route path="/admin/developer-view" element={<Protected admin><AdminDeveloperView /></Protected>} />
          <Route path="/admin/support" element={<Protected admin><AdminSupport /></Protected>} />
          <Route path="/u/:id" element={<Protected><UserProfile /></Protected>} />

          <Route path="*" element={<RootRedirect />} />
        </Routes>
        </TourProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
